/**
 * Skin Diary dashboard presentation maths — SSD-5740 (Figma 2097:25410).
 *
 * Shared by the mobile tab and the web `/skin-diary` page, which render the same 320pt-wide design
 * against the same `me/overview` payload. Only the values that MUST agree live here — bar geometry,
 * the peek fade, the caption stamp. Copy and Tailwind classes stay in each app, where they belong.
 */
import { format } from 'date-fns';
import type {
  SkinAnalysisMetricRelativeStatus,
  SkinAnalysisOverviewRoutineStep,
  SkinAnalysisOverviewScoreRange,
} from '../../model/skinAnalysis/skinAnalysis';

/** Bar band inside the 60px track, matching the design's shortest and tallest bars. */
const MIN_BAR_HEIGHT_PX = 28;
const MAX_BAR_HEIGHT_PX = 52;

/** Trailing preview rows fade progressively — the design's two peek rows, faintest last. */
const ROW_OPACITY_FROM_END: readonly number[] = [0.25, 0.5];

/** `Aug 7` — the design's short, year-less update stamp. */
const UPDATED_DATE_FORMAT = 'MMM d';

/**
 * Bar height for one score inside the result's spread. A flat result (every trait scored the same)
 * has no spread to encode, so every bar takes the full height rather than dividing by zero.
 */
export function barHeightPx(score: number, range: SkinAnalysisOverviewScoreRange): number {
  const spread = range.max - range.min;
  if (spread <= 0) return MAX_BAR_HEIGHT_PX;

  const clamped = Math.min(range.max, Math.max(range.min, score));
  const ratio = (clamped - range.min) / spread;
  return Math.round(MIN_BAR_HEIGHT_PX + ratio * (MAX_BAR_HEIGHT_PX - MIN_BAR_HEIGHT_PX));
}

/**
 * Bucket a bar renders in. The backend omits `relativeStatus` when the survey carried no supported
 * age answer, so there is no verdict to colour — those bars take the neutral middle bucket rather
 * than being promoted to a strength or demoted to a focus area.
 */
export function traitBucket(relativeStatus: SkinAnalysisMetricRelativeStatus | null): SkinAnalysisMetricRelativeStatus {
  return relativeStatus ?? 'on_par';
}

export interface RoutinePreviewRow {
  readonly step: SkinAnalysisOverviewRoutineStep;
  readonly opacity: number;
}

/**
 * Apply the peek fade to the server's preview rows.
 *
 * A routine short enough to fit entirely in the card gets no fade — there is nothing beyond it to
 * hint at, and fading the last real step would read as a rendering bug.
 */
export function routinePreviewRows(
  steps: readonly SkinAnalysisOverviewRoutineStep[],
  totalStepCount: number,
): readonly RoutinePreviewRow[] {
  const continuesPastPreview = totalStepCount > steps.length;

  return steps.map((step, index) => {
    const positionFromEnd = steps.length - 1 - index;
    const fade = continuesPastPreview ? ROW_OPACITY_FROM_END[positionFromEnd] : undefined;
    return { step, opacity: fade ?? 1 };
  });
}

/**
 * "About My Skin" caption — dates the card and says which age answer it reflects.
 *
 * `ageLabel` arrives already prefixed (`Ages 25-34`, or a self-describing `Over 60`), so it is
 * printed verbatim; when the survey carried no age answer the backend sends null and only the date
 * is shown, rather than implying an answer the customer never gave.
 */
export function aboutMySkinSubtitle(ageLabel: string | null, updatedAt: string): string {
  const updated = `updated ${format(new Date(updatedAt), UPDATED_DATE_FORMAT)}`;
  return ageLabel == null ? updated : `${ageLabel} · ${updated}`;
}
